"use client";

import {
  type GetUserQuery,
  MAIN_SITE_ROUTES,
  YmLectorEventsType,
} from "@frontend/roz-sdk-core";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@frontend/roz-uikit/studio";
import { GraduationCapIcon, LogOutIcon, UserIcon } from "lucide-react";
import { useState } from "react";

import { cn } from "@/lib/cn";
import { NavLink } from "@/primitives";
import { useAuth, useSdk, useSdkConfig, useUser } from "@/provider";

import { getAvatar } from "./internal/getAvatar";

type UserMenuUser = NonNullable<GetUserQuery["me"]>;

export interface UserMenuProps {
  profileLink?: string;
  profileLectorLink?: string;
}

export const UserMenu = ({
  profileLink = MAIN_SITE_ROUTES.profile,
  profileLectorLink = MAIN_SITE_ROUTES.profileLector,
}: UserMenuProps) => {
  const { isAuthorized, logout } = useAuth();
  const { user } = useUser<UserMenuUser>();
  const sdk = useSdk();
  const config = useSdkConfig();
  const [open, setOpen] = useState(false);

  const handleLectorClick = () => {
    sdk.analytics.sendYmAnalyticsWithDefaultParams({
      ymEvent: `${YmLectorEventsType.TARGET_AG}_events`,
      blockName: `block_${YmLectorEventsType.TARGET_AG}`,
      params: {
        eventCategory: "interactions",
        eventAction: "click",
        eventLabel: "Переход в ЛК лектора",
        eventContext: "na",
        filters: "na",
        add_info: "na",
      },
    });
    setOpen(false);
  };

  if (!(isAuthorized && user)) {
    return null;
  }

  const isDarkHeader = config.theme === "dark";
  const initials = getInitials(user);
  const avatarUrl = getAvatar(user);
  const hasSpeakerPermissions =
    "checkSpeakerPermissions" in sdk.user
      ? sdk.user.checkSpeakerPermissions(
          user as Parameters<typeof sdk.user.checkSpeakerPermissions>[0]
        )
      : false;

  return (
    <DropdownMenu modal={false} onOpenChange={setOpen} open={open}>
      <DropdownMenuTrigger asChild>
        <Button
          className={cn(
            "sdkui:w-10 sdkui:cursor-pointer sdkui:justify-center sdkui:rounded-full sdkui:border sdkui:p-1 sdkui:shadow-sm",
            "sdkui:lg:w-auto sdkui:lg:justify-start sdkui:lg:gap-2 sdkui:lg:pr-3 sdkui:lg:pl-1",
            isDarkHeader
              ? "sdkui:border-zinc-100 sdkui:bg-zinc-100"
              : "sdkui:border-dark-10 sdkui:bg-white"
          )}
          data-testid="Profile-button"
          size="lg"
          type="button"
          variant="ghostPrimary"
        >
          <Avatar className="sdkui:size-8" size="default">
            {avatarUrl && <AvatarImage src={avatarUrl} />}
            <AvatarFallback className="sdkui:bg-zinc-100 sdkui:font-medium sdkui:text-dark-80 sdkui:text-xs">
              {initials}
            </AvatarFallback>
          </Avatar>
          <span className="sdkui:hidden sdkui:font-normal sdkui:text-dark-60 sdkui:text-xs sdkui:lg:block">
            {user.firstName ?? ""}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="sdkui:w-[198px] sdkui:lg:w-[228px] sdkui:2xl:w-[244px]"
        sideOffset={8}
      >
        <DropdownMenuItem
          asChild
          className="sdkui:gap-2 sdkui:px-2 sdkui:py-1.5 sdkui:text-base"
        >
          <NavLink href={profileLink} onClick={() => setOpen(false)}>
            <UserIcon className="sdkui:size-4 sdkui:text-muted-foreground" />
            Профиль
          </NavLink>
        </DropdownMenuItem>
        {hasSpeakerPermissions && (
          <DropdownMenuItem
            asChild
            className="sdkui:gap-2 sdkui:px-2 sdkui:py-1.5 sdkui:text-base"
          >
            <NavLink href={profileLectorLink} onClick={handleLectorClick}>
              <GraduationCapIcon className="sdkui:size-4 sdkui:text-muted-foreground" />
              Кабинет лектора
            </NavLink>
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator className="sdkui:bg-zinc-100" />
        <DropdownMenuItem
          className="sdkui:gap-2 sdkui:px-2 sdkui:py-1.5 sdkui:text-base"
          onClick={async () => {
            await logout();
            setOpen(false);
          }}
          variant="destructive"
        >
          <LogOutIcon className="sdkui:size-4 sdkui:text-destructive" />
          Выйти
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const getInitials = (user: UserMenuUser) => {
  const nickname = user.nickname?.trim();

  if (nickname) {
    return nickname.slice(0, 2).toUpperCase();
  }

  const initials = [user.firstName, user.lastName]
    .filter((value): value is string => Boolean(value?.trim()))
    .map((value) => value[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  if (initials) {
    return initials;
  }

  return user.username?.slice(0, 2).toUpperCase() || "П";
};
